import { AxiosResponse } from 'axios';

import { HttpTimeoutPriority } from 'utils/enums/http-timeout-priority';
import { prepareFiltersOfSelection, prepareQPFromFilters, serverAxios } from 'utils/http';

import { IAddress } from 'models/address';
import {
  GetJobServiceResponse,
  ICancelReseravtionsRequest,
  ICancelReservationsFilters,
  ICreateReservationDefaultRequestData,
  ICreateReservationDefaultResponse,
  ICreateReservationNormalRequestData,
  ICreateReservationNormalResponse,
  ICreateReservationQuickRequestData,
  ICreateReservationQuickResponse,
  ICreateReservationRoundTripRequestData,
  ICreateReservationRoundTripResponse,
  ICreateReservationBulkRequestData,
  ICreateReservationShuttleRequestData,
  ICreateReservationShuttleResponse,
  IGetReservationsFilters,
  IGetReservationsResponse,
  IGetSingleReservationResponse,
  IUpdateReservationDefaultRequestData,
  IUpdateReservationDefaultResponse,
  IUpdateReservationNormalRequestData,
  IUpdateReservationNormalResponse,
  IUpdateReservationQuickRequestData,
  IUpdateReservationQuickResponse,
  IUpdateReservationShuttleRequestData,
  ICreateReservationShiftsResponse as ICreateReservationBulkResponse,
  IUpdateReservationShuttleResponse,
  ISetReservationStatusPickedResponse,
  ISetReservationStatusDroppedResponse,
  ISetReservationStatusNoShowResponse,
  IGetReservationMonthlyResponse,
  IGetReservationMonthlyFilters,
  IGetReservationWeeklyResponse,
  IGetReservationWeeklyFilters,
  IReservationManualyAssignDriverResponse,
  IReservationManualyAssignVehicleResponse,
  IDeleteAssignmentsFromReservationsResponse,
  IReservationsClearAssignmentsFilters,
  IReservationsClearDriversBlacklistFilters,
  IBlockWaitAndReturnReservationsFilters,
  ICancelReseravtionsResponse,
  IDeleteReseravtionsResponse,
} from 'models/http/job';
import { ServiceTypes } from 'utils/enums/service-types';
import { IGetHistoryResponse } from 'models/http/history';
import DataUploadBaseHttp from './utils/data-upload.http';
import { DataUploadModels, ModelsNames } from 'utils/enums/model-names';

class JobsHttp extends DataUploadBaseHttp {
  public static DataExportRoute = `${ModelsNames.Reservations}/data-export`;
  protected readonly route: DataUploadModels = ModelsNames.Reservations;

  public async history(id: string, abortController?: AbortController): Promise<IGetHistoryResponse> {
    return serverAxios
      .get(`/${this.route}/${id}/history`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Highest,
      })
      .then((response: AxiosResponse<IGetHistoryResponse>) => response.data);
  }

  public async get(filters?: IGetReservationsFilters, abortController?: AbortController): Promise<IGetReservationsResponse> {
    const query = prepareQPFromFilters(filters || {});

    return serverAxios
      .get(`/${this.route}?${query.toString()}`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetReservationsResponse>) => response.data);
  }

  public async getById(id: string, abortController?: AbortController): Promise<IGetSingleReservationResponse> {
    return serverAxios
      .get(`/${this.route}/single/${id}`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetSingleReservationResponse>) => response.data);
  }

  public async getMonthly(
    filters: IGetReservationMonthlyFilters,
    abortController?: AbortController,
  ): Promise<IGetReservationMonthlyResponse> {
    const query = prepareQPFromFilters(filters);

    return serverAxios
      .get(`/${this.route}/calendar/monthly?${query.toString()}`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetReservationMonthlyResponse>) => response.data);
  }

  public async getWeekly(filters: IGetReservationWeeklyFilters, abortController?: AbortController): Promise<IGetReservationWeeklyResponse> {
    const query = prepareQPFromFilters(filters);

    return serverAxios
      .get(`/${this.route}/calendar/weekly?${query.toString()}`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetReservationWeeklyResponse>) => response.data);
  }

  public async createNormal(data: ICreateReservationNormalRequestData): Promise<number> {
    return serverAxios
      .post(`/${this.route}/normal`, data, { timeout: HttpTimeoutPriority.Medium })
      .then((response: AxiosResponse<ICreateReservationNormalResponse>) => {
        return response.data.id;
      });
  }

  public async createDefault(data: ICreateReservationDefaultRequestData): Promise<number> {
    return serverAxios
      .post(`/${this.route}/quick`, data, { timeout: HttpTimeoutPriority.Medium })
      .then((response: AxiosResponse<ICreateReservationDefaultResponse>) => {
        return response.data.id;
      });
  }

  public async createRoundTrip(data: ICreateReservationRoundTripRequestData): Promise<[number, number]> {
    return serverAxios
      .post(`/${this.route}/roundTrip`, data, { timeout: HttpTimeoutPriority.Medium })
      .then((response: AxiosResponse<ICreateReservationRoundTripResponse>) => {
        return response.data.ids;
      });
  }

  public async createBulk(data: ICreateReservationBulkRequestData): Promise<number[]> {
    return serverAxios
      .post(`/${this.route}/bulk`, data, { timeout: HttpTimeoutPriority.Medium })
      .then((response: AxiosResponse<ICreateReservationBulkResponse>) => {
        return response.data.ids;
      });
  }

  public async createQuick(data: ICreateReservationQuickRequestData): Promise<number> {
    return serverAxios
      .post(`/${this.route}/quick`, data, { timeout: HttpTimeoutPriority.Medium })
      .then((response: AxiosResponse<ICreateReservationQuickResponse>) => {
        return response.data.id;
      });
  }

  public async createShuttle(data: ICreateReservationShuttleRequestData): Promise<number> {
    return serverAxios
      .post(`/${this.route}/shuttle`, data, { timeout: HttpTimeoutPriority.Medium })
      .then((response: AxiosResponse<ICreateReservationShuttleResponse>) => {
        return response.data.id;
      });
  }

  public async updateNormal(id: string, data: IUpdateReservationNormalRequestData): Promise<number> {
    return serverAxios
      .patch(`/${this.route}/${id}`, data, { timeout: HttpTimeoutPriority.Medium })
      .then((response: AxiosResponse<IUpdateReservationNormalResponse>) => {
        return response.data.id;
      });
  }

  public async updateDefault(id: string, data: IUpdateReservationDefaultRequestData): Promise<number> {
    return serverAxios
      .patch(`/${this.route}/${id}`, data, { timeout: HttpTimeoutPriority.Medium })
      .then((response: AxiosResponse<IUpdateReservationDefaultResponse>) => {
        return response.data.id;
      });
  }

  public async updateQuick(id: string, data: IUpdateReservationQuickRequestData): Promise<number> {
    return serverAxios
      .patch(`/${this.route}/${id}`, data, { timeout: HttpTimeoutPriority.Medium })
      .then((response: AxiosResponse<IUpdateReservationQuickResponse>) => {
        return response.data.id;
      });
  }

  public async updateShuttle(id: string, data: IUpdateReservationShuttleRequestData): Promise<number> {
    return serverAxios
      .patch(`/${this.route}/${id}`, data, { timeout: HttpTimeoutPriority.Medium })
      .then((response: AxiosResponse<IUpdateReservationShuttleResponse>) => {
        return response.data.id;
      });
  }

  public async setStatusPicked(id: number): Promise<number> {
    return serverAxios
      .put(`/${this.route}/${id}/status/picked`, {}, { timeout: HttpTimeoutPriority.Medium })
      .then((response: AxiosResponse<ISetReservationStatusPickedResponse>) => {
        return response.data.id;
      });
  }

  public async setStatusDropped(id: number): Promise<number> {
    return serverAxios
      .put(`/${this.route}/${id}/status/dropped`, {}, { timeout: HttpTimeoutPriority.Medium })
      .then((response: AxiosResponse<ISetReservationStatusDroppedResponse>) => {
        return response.data.id;
      });
  }

  public async setStatusNoShow(id: number): Promise<number> {
    return serverAxios
      .put(`/${this.route}/${id}/status/no-show`, {}, { timeout: HttpTimeoutPriority.Medium })
      .then((response: AxiosResponse<ISetReservationStatusNoShowResponse>) => {
        return response.data.id;
      });
  }

  public async updateSupplier(id: number, supplierId: number | null): Promise<number> {
    return serverAxios
      .patch(`/${this.route}/${id}`, { supplierId }, { timeout: HttpTimeoutPriority.Medium })
      .then((response: AxiosResponse<IUpdateReservationQuickResponse>) => {
        return response.data.id;
      });
  }

  public async cancel(filters: number[] | ICancelReservationsFilters, reason?: string): Promise<ICancelReseravtionsResponse> {
    const preparedFilters: ICancelReseravtionsRequest = prepareFiltersOfSelection(filters);

    if (reason) {
      preparedFilters.reason = reason;
    }

    return serverAxios.patch(`/${this.route}/cancel`, preparedFilters).then((response: AxiosResponse<ICancelReseravtionsResponse>) => {
      return response.data;
    });
  }

  public async delete(filters: number[] | ICancelReservationsFilters): Promise<IDeleteReseravtionsResponse> {
    const preparedFilters: ICancelReseravtionsRequest = prepareFiltersOfSelection(filters);

    return serverAxios
      .delete(`/${this.route}`, {
        data: preparedFilters,
      })
      .then((response: AxiosResponse<IDeleteReseravtionsResponse>) => {
        return response.data;
      });
  }

  public async groupJobs(filters: number[] | ICancelReservationsFilters, forceRegroup?: boolean): Promise<void> {
    const preparedFilters: ICancelReseravtionsRequest = prepareFiltersOfSelection(filters);

    return serverAxios.post(`/${this.route}/group`, { ...preparedFilters, forceRegroup }).then(() => {
      return;
    });
  }

  public async ungroupJobs(filters: number[] | ICancelReservationsFilters): Promise<void> {
    const preparedFilters: ICancelReseravtionsRequest = prepareFiltersOfSelection(filters);

    return serverAxios.post(`/${this.route}/ungroup`, preparedFilters).then(() => {
      return;
    });
  }

  public async getJobService(
    from: IAddress,
    to: IAddress,
    seats: number,
    date: number,
    abortController?: AbortController,
  ): Promise<GetJobServiceResponse> {
    console.log('request: ', from, to, seats, date, abortController);

    const result = {
      count: 2,
      data: [
        {
          id: 254424235432432,
          name: 'Van',
          type: ServiceTypes.Private,
          maxPax: 10,
        },
      ],
    };

    return Promise.resolve(result);
  }

  public async updateVehicle(id: number, vehicleId: number): Promise<IReservationManualyAssignVehicleResponse> {
    return serverAxios
      .put(
        `${this.route}/${id}/vehicle`,
        { vehicleId },
        {
          timeout: HttpTimeoutPriority.Medium,
        },
      )
      .then((response: AxiosResponse<IReservationManualyAssignVehicleResponse>) => {
        return response.data;
      });
  }

  public async updateDriver(id: number, driverId: number): Promise<IReservationManualyAssignDriverResponse> {
    return serverAxios
      .put(
        `${this.route}/${id}/driver`,
        { driverId },
        {
          timeout: HttpTimeoutPriority.Medium,
        },
      )
      .then((response: AxiosResponse<IReservationManualyAssignDriverResponse>) => {
        return response.data;
      });
  }

  public async blockWaitAndReturn(filters: number[] | IBlockWaitAndReturnReservationsFilters): Promise<void> {
    const preparedFilters = prepareFiltersOfSelection(filters);

    return serverAxios.post(`${this.route}/blockWaitAndReturn`, preparedFilters).then(() => {
      return;
    });
  }

  public async unblockWaitAndReturn(filters: number[] | IBlockWaitAndReturnReservationsFilters): Promise<void> {
    const preparedFilters = prepareFiltersOfSelection(filters);

    return serverAxios.post(`${this.route}/unblockWaitAndReturn`, preparedFilters).then(() => {
      return;
    });
  }

  public async calculateWillCallTimes(
    filters: number[] | IBlockWaitAndReturnReservationsFilters,
  ): Promise<void> {
    const preparedFilters = prepareFiltersOfSelection(filters);

    return serverAxios
      .post(`${this.route}/calculateWillCallTimes`, preparedFilters)
      .then(() => {
        return;
      });
  }

  public async clearDriverAndVehicles(ids: number[]): Promise<void>;
  public async clearDriverAndVehicles(filters: IReservationsClearAssignmentsFilters): Promise<void>;
  public async clearDriverAndVehicles(filters: number[] | IReservationsClearAssignmentsFilters): Promise<void> {
    return serverAxios
      .delete<IDeleteAssignmentsFromReservationsResponse>(`${this.route}/assignments`, {
        data: prepareFiltersOfSelection(filters),
      })
      .then(() => {
        return;
      });
  }

  /** Clear blacklisted drivers from the selected reservations (and their associated trips) */
  public async clearDriversBlacklist(ids: number[]): Promise<void>;
  public async clearDriversBlacklist(filters: IReservationsClearDriversBlacklistFilters): Promise<void>;
  public async clearDriversBlacklist(filters: number[] | IReservationsClearDriversBlacklistFilters): Promise<void> {
    return serverAxios
      .delete(`${this.route}/assignments/drivers-blacklist`, {
        data: prepareFiltersOfSelection(filters),
      })
      .then(() => {
        return;
      });
  }

  public async removeVehicle(id: number): Promise<void> {
    return serverAxios
      .delete(`${this.route}/${id}/vehicle`, {
        timeout: HttpTimeoutPriority.Medium,
      })
      .then(() => {
        return;
      });
  }
}

export default JobsHttp;
